import React from 'react';
import {Link, useLocation} from 'react-router-dom';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faSignOutAlt, faXmark} from '@fortawesome/free-solid-svg-icons';
import {useAuth} from '../../contexts/AuthContext';
import {ROLE_LABELS} from '../../utils/roles';
import {navItemsFor} from './navItems';
import {TenantAvatar} from '../UI/TenantAvatar';
import {useOverlayBackButton} from '../../hooks/useOverlayBackButton';

interface AppSidebarProps {
    isOpen: boolean;
    onClose: () => void;
    /** Số việc đang chờ, hiện thành chấm đỏ trên mục menu tương ứng */
    badges?: Partial<Record<string, number>>;
}

export const AppSidebar: React.FC<AppSidebarProps> = ({isOpen, onClose, badges = {}}) => {
    const location = useLocation();
    const {logout, user, role} = useAuth();

    const items = navItemsFor(role);

    /* Drawer cũng là một lớp phủ: Back phải đóng nó chứ không rời trang.
       `isOpen` chỉ bật khi bấm nút mở ở thanh trên - mà nút đó chỉ tồn tại dưới
       ngưỡng `lg`. Từ `lg` trở lên sidebar hiện thường trực bằng CSS
       (`lg:translate-x-0`) chứ không qua cờ này, nên máy tính không dính. */
    useOverlayBackButton(isOpen, onClose);

    const isActive = (path: string) =>
        location.pathname === path || location.pathname.startsWith(`${path}/`);

    return (
        <>
            {/* Lớp phủ khi mở drawer trên điện thoại */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
                    onClick={onClose}
                    aria-hidden="true"
                />
            )}

            <aside
                className={`fixed top-0 left-0 z-50 h-full w-[17rem] flex flex-col
                    glass-panel rounded-none
                    transform transition-transform duration-300 ease-in-out
                    ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
                aria-label="Menu chính"
            >
                {/* Đầu sidebar để trần trên nền kính, không phủ màu thương hiệu.
                    Thanh này cao 64px và luôn hiện trên mọi màn, nên tô màu vào đây
                    là biến màu đó thành thứ đông nhất trong app. Để trung tính thì
                    logo trở thành chỗ duy nhất mang màu - và vì thế nó mới nhìn ra. */}
                <div className="h-16 flex items-center justify-between gap-2 px-4 flex-shrink-0
                    border-b border-white/40 dark:border-white/10">
                    <Link to="/phong" onClick={onClose} className="flex items-center gap-3 min-w-0">
                        <img src="/images/logo.svg" alt="" className="h-8 w-8 flex-shrink-0"/>
                        <span className="min-w-0">
                            <span className="block font-bold text-lg leading-tight text-slate-900 dark:text-white">
                                Nhà 48
                            </span>
                            <span className="block text-[11px] text-slate-500 dark:text-slate-400">
                                Quản lý nhà trọ
                            </span>
                        </span>
                    </Link>
                    <button
                        onClick={onClose}
                        className="lg:hidden w-9 h-9 rounded-full flex items-center justify-center
                            text-slate-600 dark:text-slate-300
                            hover:bg-slate-900/10 dark:hover:bg-white/10 transition-colors flex-shrink-0"
                        aria-label="Đóng menu"
                    >
                        <FontAwesomeIcon icon={faXmark}/>
                    </button>
                </div>

                <nav className="flex-1 overflow-y-auto scrollbar-thin px-3 py-4 space-y-1">
                    {items.map(item => {
                        const active = isActive(item.path);
                        const badge = badges[item.path] ?? 0;

                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                onClick={onClose}
                                aria-current={active ? 'page' : undefined}
                                className={`flex items-center gap-3 px-3.5 py-3 rounded-lg text-sm font-medium
                                    transition-all duration-200
                                    ${active
                                    /* Mục đang chọn là chỗ DUY NHẤT trong khung app
                                       mặc gradient thương hiệu. Nút bấm trong nội
                                       dung vẫn đi mực slate đặc: trên nền Aurora
                                       tím, nút tím sẽ chìm vào nền, còn mực đen
                                       thì luôn tách bạch. Brand ở đây đủ để nói
                                       "bạn đang ở đâu" mà không lấn vào dữ liệu. */
                                    ? 'bg-gradient-to-br from-indigo-600 to-violet-600 text-white shadow-sm'
                                    : 'text-slate-600 dark:text-slate-300 hover:bg-white/60 dark:hover:bg-white/10'}`}
                            >
                                <FontAwesomeIcon icon={item.icon} className="w-5 h-5 flex-shrink-0"/>
                                <span className="flex-1 truncate">{item.label}</span>
                                {badge > 0 && (
                                    <span className={`min-w-[1.25rem] h-5 px-1.5 rounded-full text-[11px] font-bold
                                        flex items-center justify-center flex-shrink-0
                                        ${active ? 'bg-white/25 text-white' : 'bg-rose-500 text-white'}`}>
                                        {badge > 99 ? '99+' : badge}
                                    </span>
                                )}
                            </Link>
                        );
                    })}
                </nav>

                <div className="p-3 border-t border-white/40 dark:border-white/10 flex-shrink-0 space-y-2">
                    {user && (
                        <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg glass-soft">
                            <TenantAvatar name={user.displayName ?? undefined} email={user.email ?? undefined} size="sm"/>
                            <span className="min-w-0">
                                <span className="block text-xs font-medium text-slate-700 dark:text-slate-200 truncate">
                                    {user.displayName || user.email}
                                </span>
                                <span className="block text-[11px] text-slate-500 dark:text-slate-400 truncate">
                                    {role ? ROLE_LABELS[role] : 'Chưa cấp quyền'}
                                </span>
                            </span>
                        </div>
                    )}

                    <button
                        onClick={logout}
                        className="flex items-center gap-3 w-full px-3.5 py-2.5 rounded-lg text-sm font-medium
                            text-rose-600 dark:text-rose-400 hover:bg-rose-500/10 transition-colors"
                    >
                        <FontAwesomeIcon icon={faSignOutAlt} className="w-5 h-5"/>
                        Đăng xuất
                    </button>
                </div>
            </aside>
        </>
    );
};
